import { Injectable } from "@angular/core";
import { PRIMARY_OUTLET, Router, UrlTree } from "@angular/router";

import { BreadcrumbData, BreadcrumbItem } from "./breadcrumb.model";

@Injectable({
	providedIn: "root",
})
export class BreadcrumbService {
	constructor(private router: Router) {}

	buildList(breadcrumbData: BreadcrumbData): BreadcrumbItem[] | undefined {
		if (!breadcrumbData?.currentRoute?.externalUrl) {
			return undefined;
		}

		const urlTree = this.router.parseUrl(breadcrumbData.currentRoute.externalUrl);
		const segments = this.getUrlSegments(urlTree);
		const relativePaths = this.buildRelativePaths(segments).filter(
			(segment, index) => !this.isMarketSegment(segment, index)
		);

		const breadcrumbs = segments
			.filter((segment, index) => !this.isMarketSegment(segment, index))
			.map((segment, index) => ({
				label: segment.replace(/-/g, " "),
				route: relativePaths[index],
			}));

		if (breadcrumbData.currentRoute.data?.disableDeepBreadcrumbs) {
			return [breadcrumbData.rootItem, breadcrumbs[0]].filter((x) => x);
		}

		return [breadcrumbData.rootItem, ...breadcrumbs];
	}

	private isMarketSegment(segment: string, index: number): boolean {
		return index === 0 && segment.length === 2 && !!segment.match("^[a-z]+$");
	}

	/**
	 * Builds relative paths array based on URL segments array.
	 * @example
	 * // returns ["en", "en/promotions"]
	 * buildRelativePaths(["en", "promotions"])
	 */
	private buildRelativePaths(segments: string[]): string[] {
		return segments.reduce(
			(previous, current, index) =>
				index === 0 ? [...previous, current] : [...previous, `${previous[index - 1]}/${current}`],
			[]
		);
	}

	/**
	 * Gets URL segments from `UrlTree`.
	 * @example
	 * // returns ["en", "promotions"]
	 * getUrlSegments(UrlTree)
	 */
	private getUrlSegments(tree: UrlTree): string[] {
		if (!tree.root.children[PRIMARY_OUTLET]) {
			return [];
		}
		const { segments } = tree.root.children[PRIMARY_OUTLET];
		return segments.map(({ path }) => path.replace(/[^a-zA-Z-0-9]/g, "")).filter(Boolean);
	}
}
